/**
 * Wishlist Manager - Frontend Integration dengan Backend API
 * Mengelola wishlist customer dengan sync ke server
 */

class WishlistManager {
  constructor(customerId, shop) {
    this.customerId = customerId;
    this.shop = shop;
    this.baseUrl = ''; // Will be set from config
    this.localStorageKey = 'outerbloom_wishlist';
    this.wishlist = [];
    this.isInitialized = false;
  }

  /**
   * Initialize wishlist manager
   * Load from localStorage and sync with server if customer logged in
   */
  async init() {
    
    // Load from localStorage first
    this.loadFromLocalStorage();
    
    // If customer logged in, sync with server
    if (this.customerId && this.shop) {
      console.log('Customer logged in, syncing with server...');
      try {
        await this.syncWithServer();
        console.log('Sync completed');
      } catch (error) {
        console.warn('Sync failed, continuing with local wishlist:', error);
      }
    } else {
      console.log('No customer logged in, using local wishlist only');
    }
    
    this.isInitialized = true;
    this.updateUI();
    
    console.log('Wishlist manager initialized with', this.wishlist.length, 'items');
    
    // Dispatch initialized event
    this.dispatchEvent('wishlist:initialized', { count: this.wishlist.length });
  }

  /**
   * Load wishlist from localStorage
   */
  loadFromLocalStorage() {
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      this.wishlist = stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading wishlist from localStorage:', error);
      this.wishlist = [];
    }
  }

  /**
   * Save wishlist to localStorage
   */
  saveToLocalStorage() {
    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(this.wishlist));
    } catch (error) {
      console.error('Error saving wishlist to localStorage:', error);
    }
  }

  /**
   * Sync wishlist with server
   */
  async syncWithServer() {
    console.log(`${this.baseUrl}/api/wishlist/${this.customerId}?shop=${this.shop}`)
    if (!this.customerId || !this.shop) {
      console.log('No customer logged in, using local wishlist only');
      return;
    }

    try {
      // Get wishlist from server
      const response = await fetch(
        `${this.baseUrl}/api/wishlist/${this.customerId}?shop=${this.shop}`
      );
      
      if (!response.ok) {
        console.warn('Failed to fetch wishlist from server, using local storage');
        return; // Fallback to localStorage
      }

      const data = await response.json();
      
      if (data.success && data.data.wishlist) {
        const serverWishlist = data.data.wishlist;
        
        // Merge with local wishlist
        const merged = [...new Set([...this.wishlist, ...serverWishlist])];
        
        // If merged is different, update both local and server
        if (merged.length !== serverWishlist.length) {
          this.wishlist = merged;
          this.saveToLocalStorage();
          await this.saveToServer().catch(err => {
            console.warn('Failed to save merged wishlist to server:', err);
          });
        } else {
          this.wishlist = serverWishlist;
          this.saveToLocalStorage();
        }
      }
    } catch (error) {
      console.warn('Error syncing with server, using local storage:', error);
      // Continue with local wishlist
    }
  }

  /**
   * Save entire wishlist to server
   */
  async saveToServer() {
    if (!this.customerId || !this.shop) return;

    try {
      const response = await fetch(
        `${this.baseUrl}/api/wishlist/${this.customerId}?shop=${this.shop}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ wishlist: this.wishlist })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save wishlist to server');
      }

      const data = await response.json();
      console.log('Wishlist saved to server:', data);
    } catch (error) {
      console.error('Error saving wishlist to server:', error);
    }
  }

  /**
   * Add item to wishlist
   */
  async addItem(productHandle) {
    console.log('[Wishlist] Adding item:', productHandle);
    
    // Check if customer is logged in
    if (!this.customerId) {
      console.log('[Wishlist] Not logged in, showing tooltip');
      this.showLoginTooltip(productHandle);
      return false;
    }
    
    if (this.hasItem(productHandle)) {
      console.log('[Wishlist] Item already in wishlist');
      return false;
    }

    // Immediately update UI to show as added (optimistic update)
    this.wishlist.push(productHandle);
    this.updateButtonState(productHandle, true);
    
    try {
      this.saveToLocalStorage();
      console.log('[Wishlist] Item saved to localStorage');

    // Save to server if customer logged in
    if (this.customerId && this.shop) {
      console.log('[Wishlist] Customer logged in, syncing to server...');
      console.log('[Wishlist] Customer ID:', this.customerId);
      console.log('[Wishlist] Shop:', this.shop);
      console.log('[Wishlist] Base URL:', this.baseUrl);
      console.log('[Wishlist] API URL:', `${this.baseUrl}/api/wishlist/${this.customerId}/add?shop=${this.shop}`);
      
      try {
        console.log('[Wishlist] Sending POST request...');
        const requestBody = { productHandle };
        console.log('[Wishlist] Request body:', requestBody);
        
        const response = await fetch(
          `${this.baseUrl}/api/wishlist/${this.customerId}/add?shop=${this.shop}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody)
          }
        );

        console.log('[Wishlist] Fetch completed');
        console.log('[Wishlist] Server response status:', response.status);
        console.log('[Wishlist] Response headers:', response.headers);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.warn('[Wishlist] Failed to add item to server');
          console.warn('[Wishlist] Error response:', errorText);
          try {
            const errorData = JSON.parse(errorText);
            console.warn('[Wishlist] Error data:', errorData);
          } catch (e) {
            console.warn('[Wishlist] Could not parse error as JSON');
          }
          
          // Revert optimistic update
          const index = this.wishlist.indexOf(productHandle);
          if (index !== -1) {
            this.wishlist.splice(index, 1);
            this.saveToLocalStorage();
          }
          this.updateButtonState(productHandle, false);
          this.showErrorNotification('Failed to add item to wishlist. Please try again.');
          return false;
        } else {
          const data = await response.json();
          console.log('[Wishlist] Item successfully saved to server:', data);
        }
      } catch (error) {
        console.error('[Wishlist] Exception caught during fetch:');
        console.error('[Wishlist] Error name:', error.name);
        console.error('[Wishlist] Error message:', error.message);
        console.error('[Wishlist] Full error:', error);
        console.warn('[Wishlist] Error adding item to server:', error);
        
        // Revert optimistic update
        const index = this.wishlist.indexOf(productHandle);
        if (index !== -1) {
          this.wishlist.splice(index, 1);
          this.saveToLocalStorage();
        }
        this.updateButtonState(productHandle, false);
        this.showErrorNotification('Failed to add item to wishlist. Please try again.');
        return false;
      }
    } else {
      console.log('[Wishlist] No customer logged in, saved locally only');
      console.log('[Wishlist] Customer ID:', this.customerId, 'Shop:', this.shop);
    }

      this.updateCountBadges();
      this.dispatchEvent('wishlist:item-added', { productHandle });
      this.dispatchEvent('wishlist:updated', { count: this.wishlist.length });

      return true;
    } catch (error) {
      // If any unexpected error occurs, revert
      const index = this.wishlist.indexOf(productHandle);
      if (index !== -1) {
        this.wishlist.splice(index, 1);
        this.saveToLocalStorage();
      }
      this.updateButtonState(productHandle, false);
      console.error('[Wishlist] Unexpected error:', error);
      this.showErrorNotification('An error occurred. Please try again.');
      return false;
    }
  }

  /**
   * Remove item from wishlist
   */
  async removeItem(productHandle) {
    console.log('[Wishlist] Removing item:', productHandle);
    
    // Check if customer is logged in
    if (!this.customerId) {
      console.log('[Wishlist] Not logged in for remove action');
      window.location.href = '/account/login';
      return false;
    }
    
    const index = this.wishlist.indexOf(productHandle);
    if (index === -1) {
      console.log('[Wishlist] Item not in wishlist');
      return false;
    }

    // Immediately update UI to show as removed (optimistic update)
    this.wishlist.splice(index, 1);
    this.updateButtonState(productHandle, false);

    try {
      this.saveToLocalStorage();
      console.log('[Wishlist] Item removed from localStorage');

      // Remove from server if customer logged in
      if (this.customerId && this.shop) {
        console.log('[Wishlist] Customer logged in, syncing removal to server...');
        console.log('[Wishlist] API URL:', `${this.baseUrl}/api/wishlist/${this.customerId}/remove?shop=${this.shop}`);
        
        try {
          const response = await fetch(
            `${this.baseUrl}/api/wishlist/${this.customerId}/remove?shop=${this.shop}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ productHandle })
            }
          );

          console.log('[Wishlist] Server response status:', response.status);
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.warn('[Wishlist] Failed to remove item from server', errorData);
            
            // Revert optimistic update
            this.wishlist.push(productHandle);
            this.saveToLocalStorage();
            this.updateButtonState(productHandle, true);
            this.showErrorNotification('Failed to remove item from wishlist. Please try again.');
            return false;
          } else {
            const data = await response.json();
            console.log('[Wishlist] Item successfully removed from server:', data);
          }
        } catch (error) {
          console.warn('[Wishlist] Error removing item from server:', error);
          
          // Revert optimistic update
          this.wishlist.push(productHandle);
          this.saveToLocalStorage();
          this.updateButtonState(productHandle, true);
          this.showErrorNotification('Failed to remove item from wishlist. Please try again.');
          return false;
        }
      } else {
        console.log('[Wishlist] No customer logged in, removed locally only');
      }

      this.updateCountBadges();
      this.dispatchEvent('wishlist:item-removed', { productHandle });
      this.dispatchEvent('wishlist:updated', { count: this.wishlist.length });

      return true;
    } catch (error) {
      // If any unexpected error occurs, revert
      this.wishlist.push(productHandle);
      this.saveToLocalStorage();
      this.updateButtonState(productHandle, true);
      console.error('[Wishlist] Unexpected error:', error);
      this.showErrorNotification('An error occurred. Please try again.');
      return false;
    }
  }

  /**
   * Toggle item in wishlist
   */
  async toggleItem(productHandle) {
    console.log('[Wishlist] toggleItem called with:', productHandle);
    console.log('[Wishlist] customerId:', this.customerId);
    
    if (this.hasItem(productHandle)) {
      console.log('[Wishlist] Item already in wishlist, removing');
      return await this.removeItem(productHandle);
    } else {
      console.log('[Wishlist] Item not in wishlist, adding');
      return await this.addItem(productHandle);
    }
  }

  /**
   * Check if item is in wishlist
   */
  hasItem(productHandle) {
    return this.wishlist.indexOf(productHandle) !== -1;
  }

  /**
   * Get wishlist count
   */
  getCount() {
    return this.wishlist.length;
  }

  /**
   * Get entire wishlist
   */
  getWishlist() {
    return [...this.wishlist];
  }

  /**
   * Clear entire wishlist
   */
  async clearWishlist() {
    this.wishlist = [];
    this.saveToLocalStorage();

    // Clear on server if customer logged in
    if (this.customerId && this.shop) {
      try {
        const response = await fetch(
          `${this.baseUrl}/api/wishlist/${this.customerId}?shop=${this.shop}`,
          {
            method: 'DELETE'
          }
        );

        if (!response.ok) {
          throw new Error('Failed to clear wishlist on server');
        }
      } catch (error) {
        console.error('Error clearing wishlist on server:', error);
      }
    }

    this.updateUI();
    this.dispatchEvent('wishlist:cleared');
    this.dispatchEvent('wishlist:updated', { count: 0 });
  }

  /**
   * Update UI elements
   */
  updateUI() {
    this.updateCountBadges();
    this.updateWishlistButtons();
  }

  /**
   * Update count badges in UI
   */
  updateCountBadges() {
    const badges = document.querySelectorAll('.wishlist-count');
    const count = this.getCount();

    badges.forEach(badge => {
      badge.textContent = count;
      if (count > 0) {
        badge.classList.remove('d-none');
        badge.style.display = '';
      } else {
        badge.classList.add('d-none');
        badge.style.display = 'none';
      }
    });
  }

  /**
   * Update wishlist buttons state
   */
  updateWishlistButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-button]');
    
    buttons.forEach(button => {
      const productHandle = button.dataset.productHandle;
      if (!productHandle) return;

      this.updateButtonState(productHandle, this.hasItem(productHandle));
    });
  }

  /**
   * Update single button state
   */
  updateButtonState(productHandle, isInWishlist) {
    const button = document.querySelector(`[data-wishlist-button][data-product-handle="${productHandle}"]`);
    if (!button) return;

    if (isInWishlist) {
      button.classList.add('active', 'in-wishlist');
    } else {
      button.classList.remove('active', 'in-wishlist');
    }
  }

  /**
   * Set button loading state with spinner (DEPRECATED - not used anymore)
   */
  setButtonLoading(productHandle, isLoading) {
    // This function is deprecated and no longer used
    // Keeping it for backward compatibility
    return;
  }

  /**
   * Show error notification
   */
  showErrorNotification(message) {
    console.log('[Wishlist] showErrorNotification:', message);
    
    // Remove any existing notification
    const existingNotif = document.getElementById('wishlist-error-notification');
    if (existingNotif) {
      existingNotif.remove();
    }

    // Create notification popup
    const notification = document.createElement('div');
    notification.id = 'wishlist-error-notification';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #e74c3c;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 300px;
      max-width: 400px;
      animation: slideInRight 0.3s ease;
    `;

    notification.innerHTML = `
      <style>
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(400px);
            opacity: 0;
          }
        }
        .wishlist-error-content {
          flex: 1;
        }
        .wishlist-error-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.7;
          transition: opacity 0.2s;
        }
        .wishlist-error-close:hover {
          opacity: 1;
        }
      </style>
      <div class="wishlist-error-content">
        <div style="font-weight: 500; margin-bottom: 4px;">Error</div>
        <div style="font-size: 14px; opacity: 0.9;">
          ${message}
        </div>
      </div>
      <button class="wishlist-error-close" aria-label="Close notification">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    `;

    document.body.appendChild(notification);
    console.log('[Wishlist] Error notification displayed');

    // Close handler
    const closeNotification = () => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
    };

    // Close button click
    notification.querySelector('.wishlist-error-close').addEventListener('click', closeNotification);

    // Auto close after 4 seconds
    setTimeout(closeNotification, 4000);
  }

  /**
   * Dispatch custom event
   */
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, { detail });
    document.dispatchEvent(event);
  }

  /**
   * Show login tooltip
   */
  showLoginTooltip(productHandle) {
    console.log('[Wishlist] showLoginTooltip called for:', productHandle);
    
    // Remove any existing notification
    const existingNotif = document.getElementById('wishlist-login-notification');
    if (existingNotif) {
      existingNotif.remove();
    }

    // Create notification popup
    const notification = document.createElement('div');
    notification.id = 'wishlist-login-notification';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #212121;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 300px;
      max-width: 400px;
      animation: slideInRight 0.3s ease;
    `;

    notification.innerHTML = `
      <style>
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(400px);
            opacity: 0;
          }
        }
        .wishlist-notif-content {
          flex: 1;
        }
        .wishlist-notif-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.7;
          transition: opacity 0.2s;
        }
        .wishlist-notif-close:hover {
          opacity: 1;
        }
      </style>
      <div class="wishlist-notif-content">
        <div style="font-weight: 500; margin-bottom: 4px;">Login Required</div>
        <div style="font-size: 14px; opacity: 0.9;">
          Please <a href="/account/login" style="color: #fff; text-decoration: underline;">login</a> to save items to your wishlist
        </div>
      </div>
      <button class="wishlist-notif-close" aria-label="Close notification">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    `;

    document.body.appendChild(notification);
    console.log('[Wishlist] Notification displayed');

    // Close handler
    const closeNotification = () => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
      console.log('[Wishlist] Notification closed');
    };

    // Close button click
    notification.querySelector('.wishlist-notif-close').addEventListener('click', closeNotification);

    // Auto close after 3 seconds
    setTimeout(closeNotification, 3000);
  }
}

// Auto-initialize when DOM ready
document.addEventListener('DOMContentLoaded', function() {
  console.log('[Wishlist] DOM Ready, initializing...');
  console.log('[Wishlist] window.Shopify:', window.Shopify);
  
  // Check if customer data is available
  const customerId = window.Shopify?.customer?.id;
  let shop = window.Shopify?.shop?.name || window.Shopify?.shop?.myshopify_domain;
  
  console.log('[Wishlist] Customer ID from window.Shopify:', customerId);
  console.log('[Wishlist] Shop from window.Shopify:', shop);
  console.log('[Wishlist] Full customer object:', window.Shopify?.customer);
  
  // Fallback for shop if not set
  if (!shop || !shop.includes('myshopify.com')) {
    shop = location.hostname; // fallback ke domain saat ini
  }

  // Initialize manager
  const baseUrl = window.WISHLIST_API_URL || 'http://localhost:5001';
  window.wishlistManager = new WishlistManager(customerId, shop);
  window.wishlistManager.baseUrl = baseUrl;
  
  // Validate configuration
  if (!window.wishlistManager.baseUrl) {
    console.error('[Wishlist] ERROR: baseUrl is not set! API calls will fail.');
  }
  if (window.wishlistManager.customerId && !window.wishlistManager.shop) {
    console.error('[Wishlist] ERROR: Customer logged in but shop is not set!');
  }

  // Initialize
  window.wishlistManager.init().then(() => {
    console.log('[Wishlist] Manager initialized successfully');
  });

  // Setup button click handlers
  // Try jQuery first if available, fallback to vanilla JS
  if (typeof $ !== 'undefined') {
    $(document).on('click', '[data-wishlist-button]', async function(e) {
      
      e.preventDefault();
      e.stopPropagation();

      const $button = $(this);
      const productHandle = $button.data('productHandle');
      
      console.log('[Wishlist] Product handle from jQuery:', productHandle);
      if (!productHandle) {
        console.warn('[Wishlist] Button clicked but no product handle found');
        return;
      }

      console.log('[Wishlist] Button clicked for product:', productHandle);

      try {
        await window.wishlistManager.toggleItem(productHandle);
        console.log('[Wishlist] Toggle completed successfully');
      } catch (error) {
        console.error('[Wishlist] Error toggling wishlist item:', error);
        // Error is already handled in addItem/removeItem with revert
      }
    });
  } else {
    console.log('[Wishlist] jQuery not available, using vanilla JS delegation');
    document.addEventListener('click', async function(e) {
      const button = e.target.closest('[data-wishlist-button]');
      if (!button) return;

      console.log('[Wishlist] Vanilla JS click event on wishlist button');
      e.preventDefault();
      e.stopPropagation();

      const productHandle = button.dataset.productHandle;
      console.log('[Wishlist] Product handle from vanilla JS:', productHandle);
      if (!productHandle) {
        console.warn('[Wishlist] Button clicked but no product handle found');
        return;
      }

      console.log('[Wishlist] Button clicked for product:', productHandle);
      try {
        await window.wishlistManager.toggleItem(productHandle);
        console.log('[Wishlist] Toggle completed successfully');
      } catch (error) {
        console.error('[Wishlist] Error toggling wishlist item:', error);
        // Error is already handled in addItem/removeItem with revert
      }
    });
  }
});
// Backward compatibility with old Wishlist object
window.Wishlist = {
  getWishlist: function() {
    return window.wishlistManager ? window.wishlistManager.getWishlist() : [];
  },
  isInWishlist: function(productHandle) {
    return window.wishlistManager ? window.wishlistManager.hasItem(productHandle) : false;
  },
  toggleWishlistItem: function(productHandle) {
    if (window.wishlistManager) {
      window.wishlistManager.toggleItem(productHandle);
    }
  },
  addToWishlist: function(productHandle) {
    if (window.wishlistManager) {
      window.wishlistManager.addItem(productHandle);
    }
  },
  removeFromWishlist: function(productHandle) {
    if (window.wishlistManager) {
      window.wishlistManager.removeItem(productHandle);
    }
  },
  updateWishlistCount: function() {
    if (window.wishlistManager) {
      window.wishlistManager.updateCountBadges();
    }
  }
};
